/**
 * currentFactor.js — PROD-1154 (patient half). A CURRENT CODE BEFORE 2FA OR THE SIGN-IN NUMBER IS CHANGED.
 *
 * With two-factor ON, "Change verification method" (POST /auth/2fa/setup), "Regenerate backup codes"
 * (POST /auth/2fa/regenerate-codes), POST /auth/2fa/reset and a move of an SMS account's sign-in number
 * (POST /auth/phone/verify) needed only the session, so anyone holding an unlocked phone could enrol their
 * own authenticator, take ten fresh backup codes or move the number the codes go to. The API (PROD-1154,
 * the patient twin of PROD-1075 / PROD-1161) now refuses those while 2FA is on unless the body carries
 * `currentCode`:
 *   - a 6-digit code of the CURRENT method (authenticator app, or the text POST /auth/2fa/send-current-code
 *     sends to an SMS account), or
 *   - one unused backup code: 8 characters a-z / 0-9 (it is spent).
 * The first setup call of a change returns a `setupToken` that carries the proof (apiFetch stores it and
 * setup2fa sends it back), so a method switch or an SMS resend inside the same change does not ask again.
 * The backup-code sign-in also returns one, so its re-enrolment asks nothing. 2FA-off accounts send nothing new.
 *
 * ★ REACTIVE EVERYWHERE (review of d6508d36, M1). The app ships before the API, and the live API (v243.369.0)
 * neither asks for a code nor has /auth/2fa/send-current-code. So every flow first sends its request WITHOUT
 * a code, exactly as before; the field (and its "Text me a code") shows only once the API answers 403
 * 2fa_current_factor_required, which uses nothing up. On the live API the request simply succeeds.
 *
 * Refusals are keyed on the API's `code`, never its text. The same pattern as the md panel
 * (lib/currentFactor.js there); the patient app says each sentence through its own i18n keys.
 * Pure: no React, no fetch. Gate: scripts/check-patient-2fa-current-code.mjs.
 */

export const CF_REQUIRED = '2fa_current_factor_required'
export const CF_INVALID = '2fa_current_factor_invalid'
export const CF_UNCONFIRMED = '2fa_current_factor_unconfirmed'
export const CURRENT_FACTOR_CODES = Object.freeze([CF_REQUIRED, CF_INVALID, CF_UNCONFIRMED])

/** The sentence for a refusal code, through the app's t(). The md panel's wording; existing keys where one says it. */
export function currentFactorMessage(t, code) {
  if (code === CF_REQUIRED) {
    return t('security.currentCodeRequired', 'Two-factor authentication is already on. Enter a current verification code to change it.')
  }
  if (code === CF_INVALID) return t('auth.invalidCode', 'Invalid verification code')
  return t('errors2.failedToSave', 'Failed to save. Please try again.')
}

/** The lockout body (the sign-in lockout's title sits above it: signIn.accountTemporarilyLocked). */
export function lockMessage(t, seconds) {
  return t('security.currentCodeLocked', { time: formatLockTime(seconds), defaultValue: 'Too many failed attempts. Try again in {{time}}.' })
}

export const formatLockTime = (s) => {
  const n = Math.max(0, Math.floor(Number(s) || 0))
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`
}

/** What the field keeps of a keystroke: spaces removed, at most 8 characters (a backup code). */
export const currentCodeInput = (value) => String(value ?? '').replace(/\s+/g, '').slice(0, 8)

/** The code as sent: as typed, spaces removed (the server trims and lower-cases backup codes). */
export const normalizeCurrentCode = (value) => String(value ?? '').replace(/\s+/g, '')

/** A 6-digit authenticator / text code, or an 8-character a-z / 0-9 backup code (either case). */
export const isCurrentCodeShaped = (value) => {
  const v = normalizeCurrentCode(value)
  return /^\d{6}$/.test(v) || /^[a-z0-9]{8}$/i.test(v)
}

/**
 * Before a call: null to go ahead, else the refusal code whose sentence to show. Nothing is sent when the
 * code is empty or cannot be a code, so a typo does not spend one of the five attempts.
 */
export function currentCodeProblem(asking, value) {
  if (!asking) return null
  const v = normalizeCurrentCode(value)
  if (!v) return CF_REQUIRED
  if (!isCurrentCodeShaped(v)) return CF_INVALID
  return null
}

/**
 * After a refused call: null when the error is none of these (the caller keeps its own handling), else
 *   { locked: true, seconds }        the sign-in lockout (429 locked + remainingSeconds)
 *   { code, ask }                    a mapped refusal: `ask` = show the code field
 * Whether the typed code is emptied is currentCodeMayBeSpent's call, for refusals and other errors alike.
 */
export function currentFactorRefusal(err) {
  if (!err) return null
  const seconds = Number(err.remainingSeconds)
  if (err.status === 429 && err.locked && seconds > 0) return { locked: true, seconds }
  const code = typeof err.code === 'string' ? err.code : null
  if (!code || !CURRENT_FACTOR_CODES.includes(code)) return null
  return { code, ask: code !== CF_UNCONFIRMED }
}

/** The sentence above the field after a refusal: none for 403 required (the field's own line says it). */
export const refusalSentence = (t, code) => (code === CF_REQUIRED ? '' : currentFactorMessage(t, code))

// The refusals the API gives BEFORE it looks at currentCode: the code was not used, so it stays in the field.
// Review L-5: 'rate_limited' is the per-IP 429, answered before any view runs.
const BEFORE_FACTOR_CHECK = Object.freeze([CF_REQUIRED, 'sms_us_ca_only', 'phone_code_invalid', 'rate_limited'])

/**
 * After a call that CARRIED a current code failed: true when that code may have been used up, so the caller
 * empties the field and a retry cannot spend an attempt on a used code (review L1). Only the refusals the API
 * gives before the current-factor check keep it (403 required, the US/Canada number refusal, a wrong
 * new-number code, the per-IP 429 rate_limited). Everything else may come after the code was accepted: a wrong code, the lockout, 500
 * unconfirmed, the regenerate 500, a timeout (408) or a dropped connection (0).
 */
export function currentCodeMayBeSpent(err) {
  if (!err) return false
  if (err.smsRefused === 'sms_us_ca_only') return false
  return !(typeof err.code === 'string' && BEFORE_FACTOR_CHECK.includes(err.code))
}
